import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { firebaseAuth, checkSsoMethod, recordAuthEvent } from "@/firebase";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import gptDriverLogoWhite from "../assets/images/gpt-driver-logo-white.png";

const SAML_LOGIN_URL =
  "https://us-central1-flutter-feedback.cloudfunctions.net/samlLogin";

const emailSchema = z.email("Invalid email address");
const loginPasswordSchema = z.string().min(1, "Password is required");
const registerPasswordSchema = z
  .string()
  .min(6, "Password must be at least 6 characters");
const nameSchema = z.string().min(2, "Name must be at least 2 characters");

const googleProvider = new GoogleAuthProvider();

const zodValidate =
  (schema: z.ZodType) =>
  ({ value }: { value: string }) => {
    const result = schema.safeParse(value);
    return result.success ? undefined : result.error.issues[0].message;
  };

const confirmPasswordValidate = ({
  value,
  fieldApi,
}: {
  value: string;
  fieldApi: any;
}) => {
  if (!value) return "Please confirm your password";
  if (value !== fieldApi.form.getFieldValue("password"))
    return "Passwords don't match";
  return undefined;
};

function getFirebaseErrorMessage(error: unknown): string {
  const code = (error as any)?.code as string | undefined;
  const message = (error as any)?.message as string | undefined;
  if (
    code === "auth/internal-error" &&
    message?.includes("Unauthorized email")
  ) {
    return "This email is not authorized to create an account.";
  }
  switch (code) {
    case "auth/invalid-credential":
    case "auth/user-not-found":
    case "auth/wrong-password":
      return "Invalid email or password.";
    case "auth/email-already-in-use":
      return "An account with this email already exists.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please try again later.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    default:
      return "An error occurred. Please try again.";
  }
}

/**
 * Best-effort report of a FAILED login to the audit trail. Never throws — a
 * reporting failure must not affect the user's login UX. Success is captured
 * server-side by the beforeUserSignedIn blocking function, so we only report
 * failures here.
 */
function reportLoginFailure(params: {
  email?: string;
  error: unknown;
  method: string;
}): void {
  const code = (params.error as any)?.code as string | undefined;
  void recordAuthEvent({
    event_type: "login.failed",
    email: params.email,
    error_code: code,
    method: params.method,
  }).catch(() => {
    /* swallow — audit reporting is best-effort */
  });
}

interface LoginScreenProps {
  /**
   * If set, the right-panel title becomes "Sign in to connect <name>"
   * to make the OAuth-handshake context clear (used when a third-party
   * MCP client kicked the user over to the webapp to authenticate).
   */
  connectingClientName?: string;
}

const LoginScreen = ({ connectingClientName }: LoginScreenProps = {}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  // "email" = first step, "password" = second step after SSO check returned "password"
  const [loginStep, setLoginStep] = useState<"email" | "password">("email");
  const [emailError, setEmailError] = useState<string | undefined>();
  const [isRedirectingToSso, setIsRedirectingToSso] = useState(false);

  const signInWithEmailMutation = useMutation({
    mutationFn: async ({
      email,
      password,
    }: {
      email: string;
      password: string;
    }) => await signInWithEmailAndPassword(firebaseAuth, email, password),
    onError: (error, variables) =>
      reportLoginFailure({
        email: variables.email,
        error,
        method: "password",
      }),
  });

  const registerMutation = useMutation({
    mutationFn: async ({
      name,
      email,
      password,
    }: {
      name: string;
      email: string;
      password: string;
    }) => {
      const cred = await createUserWithEmailAndPassword(
        firebaseAuth,
        email,
        password
      );
      await updateProfile(cred.user, { displayName: name });
      return cred;
    },
    onError: (error, variables) =>
      reportLoginFailure({ email: variables.email, error, method: "register" }),
  });

  const signInWithGoogleMutation = useMutation({
    mutationFn: async () => await signInWithPopup(firebaseAuth, googleProvider),
    onError: (error) =>
      reportLoginFailure({
        email: (error as any)?.customData?.email,
        error,
        method: "google",
      }),
  });

  const checkSsoMutation = useMutation({
    mutationFn: async (email: string) => {
      const result = await checkSsoMethod({ email });
      return result.data;
    },
    onSuccess: (data, email) => {
      if (data.method === "saml") {
        const domain = email.split("@")[1];
        setIsRedirectingToSso(true);
        window.location.href = `${SAML_LOGIN_URL}?domain=${encodeURIComponent(domain)}&redirectPath=${encodeURIComponent(window.location.pathname)}`;
      } else {
        setLoginStep("password");
      }
    },
  });

  const loginForm = useForm({
    defaultValues: { email: "", password: "" },
    onSubmit: async ({ value }) => {
      await signInWithEmailMutation.mutateAsync(value);
    },
  });

  const registerForm = useForm({
    defaultValues: { name: "", email: "", password: "", confirmPassword: "" },
    onSubmit: async ({ value }) => {
      await registerMutation.mutateAsync(value);
    },
  });

  const handleEmailContinue = () => {
    const email = loginForm.getFieldValue("email");
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setEmailError(result.error.issues[0].message);
      return;
    }
    setEmailError(undefined);
    checkSsoMutation.mutate(email);
  };

  const handleBackToEmail = () => {
    setLoginStep("email");
    setEmailError(undefined);
    checkSsoMutation.reset();
    signInWithEmailMutation.reset();
  };

  const switchToRegister = () => {
    setIsRegistering(true);
    setLoginStep("email");
    setEmailError(undefined);
    signInWithEmailMutation.reset();
    signInWithGoogleMutation.reset();
    checkSsoMutation.reset();
    loginForm.reset();
  };

  const switchToLogin = () => {
    setIsRegistering(false);
    registerMutation.reset();
    registerForm.reset();
  };

  const isAnyLoading = isRegistering
    ? registerMutation.isPending
    : signInWithEmailMutation.isPending ||
      signInWithGoogleMutation.isPending ||
      checkSsoMutation.isPending;

  const googleError =
    (signInWithGoogleMutation.error as any)?.code !==
    "auth/popup-closed-by-user"
      ? signInWithGoogleMutation.error
      : null;

  const authError = isRegistering
    ? registerMutation.error
    : signInWithEmailMutation.error ?? googleError ?? checkSsoMutation.error;

  if (isRedirectingToSso) {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          bgcolor: "white",
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Redirecting to your SSO provider...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ height: "100%", width: "100%", display: "flex", bgcolor: "white" }}
    >
      {/* Left branding panel */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: 400,
          flexShrink: 0,
          justifyContent: "center",
          bgcolor: "#4C63FF",
          color: "white",
          p: 4,
        }}
      >
        <Box
          component="img"
          src={gptDriverLogoWhite}
          alt="GPT Driver"
          sx={{ width: 128, mb: 5 }}
        />
        <Typography
          variant="h4"
          sx={{ mb: 3.5, fontWeight: 600, lineHeight: 1.5, color: "white" }}
        >
          Boost productivity
          <br />
          with AI doing
          <br />
          mobile app QA
        </Typography>
        <Typography sx={{ lineHeight: 1.75, color: "white" }}>
          Test more of your app, with greater accuracy, across more devices
          <br /> at a fraction of the cost.
        </Typography>
      </Box>

      {/* Right panel */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, mb: 3, textAlign: "center" }}
          >
            {isRegistering
              ? "Create your account"
              : connectingClientName
                ? `Sign in to connect ${connectingClientName}`
                : "Sign in to GPT Driver"}
          </Typography>
          {connectingClientName && !isRegistering && (
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              {connectingClientName} is requesting access to your GPT Driver
              workspace.
            </Typography>
          )}

          {authError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {getFirebaseErrorMessage(authError)}
            </Alert>
          )}

          {isRegistering ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void registerForm.handleSubmit();
              }}
            >
              <registerForm.Field
                name="name"
                validators={{
                  onBlur: zodValidate(nameSchema),
                  onSubmit: zodValidate(nameSchema),
                }}
              >
                {(field) => (
                  <TextField
                    fullWidth
                    label="Full name"
                    value={field.state.value}
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                    }}
                    onBlur={field.handleBlur}
                    error={
                      (field.state.meta.isTouched ||
                        registerForm.state.submissionAttempts > 0) &&
                      field.state.meta.errors.length > 0
                    }
                    helperText={
                      field.state.meta.isTouched ||
                      registerForm.state.submissionAttempts > 0
                        ? (field.state.meta.errors[0] as string)
                        : undefined
                    }
                    sx={{ mb: 1.5 }}
                  />
                )}
              </registerForm.Field>
              <registerForm.Field
                name="email"
                validators={{
                  onBlur: zodValidate(emailSchema),
                  onSubmit: zodValidate(emailSchema),
                }}
              >
                {(field) => (
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={field.state.value}
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                    }}
                    onBlur={field.handleBlur}
                    error={
                      (field.state.meta.isTouched ||
                        registerForm.state.submissionAttempts > 0) &&
                      field.state.meta.errors.length > 0
                    }
                    helperText={
                      field.state.meta.isTouched ||
                      registerForm.state.submissionAttempts > 0
                        ? (field.state.meta.errors[0] as string)
                        : undefined
                    }
                    sx={{ mb: 1.5 }}
                  />
                )}
              </registerForm.Field>

              <registerForm.Field
                name="password"
                validators={{
                  onBlur: zodValidate(registerPasswordSchema),
                  onSubmit: zodValidate(registerPasswordSchema),
                }}
              >
                {(field) => (
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={field.state.value}
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                    }}
                    onBlur={field.handleBlur}
                    error={
                      (field.state.meta.isTouched ||
                        registerForm.state.submissionAttempts > 0) &&
                      field.state.meta.errors.length > 0
                    }
                    helperText={
                      field.state.meta.isTouched ||
                      registerForm.state.submissionAttempts > 0
                        ? (field.state.meta.errors[0] as string)
                        : undefined
                    }
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => {
                                setShowPassword((p) => !p);
                              }}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ mb: 1.5 }}
                  />
                )}
              </registerForm.Field>

              <registerForm.Field
                name="confirmPassword"
                validators={{
                  onChangeListenTo: ["password"],
                  onChange: confirmPasswordValidate,
                  onBlur: confirmPasswordValidate,
                  onSubmit: confirmPasswordValidate,
                }}
              >
                {(field) => (
                  <TextField
                    fullWidth
                    label="Confirm password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={field.state.value}
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                    }}
                    onBlur={field.handleBlur}
                    error={
                      (field.state.meta.isTouched ||
                        registerForm.state.submissionAttempts > 0) &&
                      field.state.meta.errors.length > 0
                    }
                    helperText={
                      field.state.meta.isTouched ||
                      registerForm.state.submissionAttempts > 0
                        ? (field.state.meta.errors[0] as string)
                        : undefined
                    }
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => {
                                setShowConfirmPassword((p) => !p);
                              }}
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ mb: 2 }}
                  />
                )}
              </registerForm.Field>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isAnyLoading}
                sx={{ py: 1.5, mb: 2 }}
              >
                {registerMutation.isPending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Create account"
                )}
              </Button>

              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  color: "text.secondary",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "baseline",
                  gap: "0.25rem",
                }}
              >
                Already have an account?{" "}
                <Button variant="text" size="small" onClick={switchToLogin}>
                  Sign in
                </Button>
              </Typography>
            </form>
          ) : (
            <>
              {/* Step 1: email only */}
              {loginStep === "email" && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleEmailContinue();
                  }}
                >
                  <loginForm.Field
                    name="email"
                    validators={{ onBlur: zodValidate(emailSchema) }}
                  >
                    {(field) => (
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={field.state.value}
                        onChange={(e) => {
                          field.handleChange(e.target.value);
                          setEmailError(undefined);
                        }}
                        onBlur={field.handleBlur}
                        error={
                          !!emailError ||
                          (field.state.meta.isTouched &&
                            field.state.meta.errors.length > 0)
                        }
                        helperText={
                          emailError ??
                          (field.state.meta.isTouched
                            ? (field.state.meta.errors[0] as string)
                            : undefined)
                        }
                        sx={{ mb: 1.5 }}
                      />
                    )}
                  </loginForm.Field>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isAnyLoading}
                    sx={{ py: 1.5, mb: 2 }}
                  >
                    {checkSsoMutation.isPending ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Continue"
                    )}
                  </Button>
                </form>
              )}

              {/* Step 2: password (shown after SSO check returned "password") */}
              {loginStep === "password" && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void loginForm.handleSubmit();
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      mb: 1.5,
                      px: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {loginForm.getFieldValue("email")}
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleBackToEmail}
                    >
                      Change
                    </Button>
                  </Box>

                  <loginForm.Field
                    name="password"
                    validators={{
                      onBlur: zodValidate(loginPasswordSchema),
                      onSubmit: zodValidate(loginPasswordSchema),
                    }}
                  >
                    {(field) => (
                      <TextField
                        fullWidth
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        value={field.state.value}
                        onChange={(e) => {
                          field.handleChange(e.target.value);
                        }}
                        onBlur={field.handleBlur}
                        error={
                          (field.state.meta.isTouched ||
                            loginForm.state.submissionAttempts > 0) &&
                          field.state.meta.errors.length > 0
                        }
                        helperText={
                          field.state.meta.isTouched ||
                          loginForm.state.submissionAttempts > 0
                            ? (field.state.meta.errors[0] as string)
                            : undefined
                        }
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => {
                                    setShowPassword((p) => !p);
                                  }}
                                  edge="end"
                                >
                                  {showPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                        sx={{ mb: 2 }}
                        autoFocus
                      />
                    )}
                  </loginForm.Field>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isAnyLoading}
                    sx={{ py: 1.5, mb: 2 }}
                  >
                    {signInWithEmailMutation.isPending ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Sign in"
                    )}
                  </Button>
                </form>
              )}

              {/* Google sign-in — only on email step */}
              {loginStep === "email" && (
                <>
                  <Divider sx={{ my: 2 }}>or</Divider>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={
                      signInWithGoogleMutation.isPending ? undefined : (
                        <GoogleIcon />
                      )
                    }
                    onClick={() => {
                      signInWithGoogleMutation.mutate();
                    }}
                    disabled={isAnyLoading}
                    sx={{ py: 1.5, mb: 3 }}
                  >
                    {signInWithGoogleMutation.isPending ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Sign in with Google"
                    )}
                  </Button>
                </>
              )}

              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  color: "text.secondary",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "baseline",
                  gap: "0.25rem",
                  mt: loginStep === "password" ? 1 : 0,
                }}
              >
                Don&apos;t have an account?{" "}
                <Button variant="text" size="small" onClick={switchToRegister}>
                  Create one
                </Button>
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const GoogleIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      fill="#4285F4"
    />
    <path
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      fill="#34A853"
    />
    <path
      d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"
      fill="#EA4335"
    />
  </svg>
);

export { LoginScreen };
